import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { analytics } from "../../analytics/analytics";
import { api } from "../../api/client";
import { Button, PageHeader, Skeleton, StatePanel } from "../../components/ui";
import { t } from "../../i18n";
import { useAppStore } from "../../state/appStore";

export function VenueReservePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { authenticated, draft } = useAppStore();
  const venue = useQuery({
    queryKey: ["venue", id],
    queryFn: () => api.venue(id!),
    enabled: !!id,
  });
  const [leadGuestName, setLeadGuestName] = useState("");
  const [date, setDate] = useState(draft.date);
  const [time, setTime] = useState(draft.startTime);
  const [partySize, setPartySize] = useState(draft.adults + draft.children);
  const [occasion, setOccasion] = useState("");
  const [specialRequests, setSpecialRequests] = useState("");
  const mutation = useMutation({
    mutationFn: () =>
      api.createVenueReservation({
        venueId: id!,
        requestedLocalDate: date,
        requestedLocalTime: time,
        partySize,
        leadGuestName,
        verifiedMobile: "+961 71 234 567",
        ...(occasion ? { occasion } : {}),
        ...(specialRequests ? { specialRequests } : {}),
      }),
    onSuccess: async (result) => {
      analytics.track("venue_booking_submitted", {
        bookingMode: "request_to_book",
      });
      await queryClient.invalidateQueries({ queryKey: ["reservations"] });
      navigate(`/reservations/${result.id}`);
    },
  });
  if (venue.isLoading)
    return (
      <div className="page">
        <Skeleton height={80} />
        <Skeleton height={420} />
      </div>
    );
  if (!venue.data)
    return (
      <div className="page">
        <StatePanel
          title="Place not found"
          body="Return to Reserve and choose another place."
        />
      </div>
    );
  if (!venue.data.hasRequestToBook)
    return (
      <div className="page">
        <PageHeader back={() => navigate(-1)} title={venue.data.venueName} />
        <StatePanel
          title={t.directBooking.notRequired}
          body={venue.data.cancellationPolicy}
          action={
            <Link className="button button--primary" to="/explore">
              Back to Reserve
            </Link>
          }
        />
      </div>
    );
  if (!authenticated)
    return (
      <div className="page">
        <PageHeader
          back={() => navigate(-1)}
          eyebrow={t.directBooking.eyebrow}
          title={t.directBooking.signInTitle}
        />
        <p>{t.directBooking.signInBody}</p>
        <Link
          className="button button--primary"
          to={`/auth?returnTo=${encodeURIComponent(`/venues/${id}/reserve`)}`}
        >
          Sign in and continue
        </Link>
      </div>
    );
  return (
    <div className="page direct-booking-page">
      <PageHeader
        back={() => navigate(-1)}
        eyebrow={t.directBooking.eyebrow}
        title={t.directBooking.title}
      />
      <div className="booking-place-summary">
        <img src={venue.data.image} alt="" />
        <div>
          <strong>{venue.data.venueName}</strong>
          <p>
            {venue.data.branchName} · {venue.data.area}
          </p>
        </div>
      </div>
      <div className="notice">
        <strong>Request to book</strong>
        <p>{t.directBooking.independent}</p>
      </div>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          analytics.track("venue_booking_started");
          mutation.mutate();
        }}
      >
        <div className="form-grid">
          <label>
            {t.directBooking.date}
            <input
              type="date"
              min={new Date().toISOString().slice(0, 10)}
              value={date}
              onChange={(event) => setDate(event.target.value)}
              required
            />
          </label>
          <label>
            {t.directBooking.time}
            <input
              type="time"
              value={time}
              onChange={(event) => setTime(event.target.value)}
              required
            />
          </label>
        </div>
        <label>
          {t.directBooking.partySize}
          <input
            type="number"
            min="1"
            max="20"
            value={partySize}
            onChange={(event) => setPartySize(Number(event.target.value))}
            required
          />
        </label>
        <label>
          {t.directBooking.leadGuest}
          <input
            value={leadGuestName}
            onChange={(event) => setLeadGuestName(event.target.value)}
            autoComplete="name"
            required
          />
        </label>
        <label>
          {t.directBooking.verifiedMobile}
          <input value="+961 71 234 567" disabled />
          <small className="field-hint">Verified</small>
        </label>
        <label>
          {t.directBooking.occasion}
          <select
            value={occasion}
            onChange={(event) => setOccasion(event.target.value)}
          >
            <option value="">No occasion</option>
            <option value="birthday">Birthday</option>
            <option value="anniversary">Anniversary</option>
            <option value="celebration">Celebration</option>
          </select>
        </label>
        <label>
          {t.directBooking.requests}
          <textarea
            maxLength={500}
            value={specialRequests}
            onChange={(event) => setSpecialRequests(event.target.value)}
            placeholder="Dietary needs, accessibility or seating requests"
          />
        </label>
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? "Sending request…" : t.directBooking.submit}
        </Button>
        {mutation.isError && (
          <p className="field-error" role="alert">
            {mutation.error instanceof Error
              ? mutation.error.message
              : t.directBooking.unavailable}
          </p>
        )}
        <p className="legal-copy">
          No deposit is charged now. If the place accepts and requires a
          deposit, Spot will ask you to review and pay it through Whish before
          confirmation.
        </p>
      </form>
    </div>
  );
}
